import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  ApiError,
  apiDownload,
  apiRequest
} from "../../api/client";
import { useDialogFocus } from "../../app/useDialogFocus";
import {
  getProjectRepository,
  ProjectRepositoryError
} from "../../data/projectRepository";
import type { TimetableVersionRecord } from "../../storage/timetableVersionStore";
import {
  ConfirmModal,
  EditorModal,
  ErrorText
} from "../catalog/CatalogPages";
import { ContextHeader } from "../shared/project-context";
import { AssistantMultiSelect } from "../shared/AssistantMultiSelect";
import { TeacherSearchSelect } from "../shared/TeacherSearchSelect";
import {
  type WeekParity,
  type WeekPattern,
  weekParityLabel,
  weekPatternLabel,
  weekPatternOptions
} from "../shared/weekPatterns";
import { ScheduleOverview, TimetableGrid } from "./TimetableDnd";
import "./timetable.css";
import type {
  DragOptionsResponse,
  PlacementAction,
  TimetableLesson,
  TimetableReference,
  TimetableSchedule,
  TimetableSlot,
  TimetableView,
  TimetableViewResponse
} from "./types";

interface CourseItem {
  id: string;
  name: string;
}

interface TeacherItem {
  id: string;
  name: string;
}

interface ClassItem {
  id: string;
  grade_label: string;
  class_name: string;
}

interface RoomInstance {
  id: string;
  display_name: string;
}

interface RoomItem {
  instances: RoomInstance[];
}

interface CatalogList<T> {
  items: T[];
}

interface LessonForm {
  weekday: number;
  timeSlotId: string;
  courseId: string;
  classId: string;
  mainTeacherId: string;
  assistantTeacherIds: string[];
  roomInstanceId: string;
  weekPattern: WeekPattern;
}

interface EditorState {
  schedule: TimetableSchedule;
  lesson: TimetableLesson | null;
  expectedRevision: number;
}

interface DeleteState {
  lesson: TimetableLesson;
  expectedRevision: number;
}

interface ExportRequestState {
  scope: "CURRENT" | "ALL";
}

type WriteCommand =
  | {
      kind: "SAVE";
      lessonId: string | null;
      expectedRevision: number;
      confirmRuleWarnings: boolean;
      payload: LessonForm;
      contextObjectId: string;
    }
  | {
      kind: "DELETE";
      lessonId: string;
      expectedRevision: number;
    }
  | {
      kind: "MOVE";
      action: PlacementAction;
      contextObjectId: string;
      confirmRuleWarnings: boolean;
    }
  | {
      kind: "SWAP";
      action: PlacementAction;
      contextObjectId: string;
      confirmRuleWarnings: boolean;
    };

const TIMETABLE_VIEWS: ReadonlyArray<{
  view: TimetableView;
  segment: string;
  label: string;
}> = [
  { view: "CLASS", segment: "classes", label: "班级" },
  { view: "TEACHER", segment: "teachers", label: "教师" },
  { view: "ROOM", segment: "rooms", label: "功能教室" }
];

interface ActionConfirmation {
  title: string;
  description: string;
  command: WriteCommand;
}

export function TimetablePage({
  projectId,
  view
}: {
  projectId: string;
  view: TimetableView;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  // null = 尚未手动选择（默认展示第一个对象）；"" = 全部对象
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(
    null
  );
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [weekParity, setWeekParity] = useState<WeekParity>("ODD");
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [form, setForm] = useState<LessonForm | null>(null);
  const [deleting, setDeleting] = useState<DeleteState | null>(null);
  const [confirmation, setConfirmation] =
    useState<ActionConfirmation | null>(null);
  const [exportConfirmation, setExportConfirmation] =
    useState<ExportRequestState | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    versionIdFromLocation
  );
  const [versionDetailsOpen, setVersionDetailsOpen] = useState(false);

  useEffect(() => {
    setSelectedObjectId(null);
    setOverviewOpen(false);
    setWeekParity("ODD");
    setEditor(null);
    setForm(null);
    setDeleting(null);
    setConfirmation(null);
    setExportConfirmation(null);
    setSelectedVersionId(versionIdFromLocation());
    setVersionDetailsOpen(false);
  }, [projectId, view]);

  const versions = useQuery({
    queryKey: ["timetable-versions", projectId],
    queryFn: async () => (await getProjectRepository()).timetableVersions(projectId)
  });
  const versionHead = useQuery({
    queryKey: ["timetable-version-head", projectId],
    queryFn: async () => (await getProjectRepository()).timetableVersionHead(projectId)
  });
  const activeVersionId = versionHead.data?.version_id ?? null;
  const effectiveVersionId = selectedVersionId ?? activeVersionId;
  const selectedVersion =
    versions.data?.find((item) => item.id === effectiveVersionId) ?? null;
  const historicalVersion =
    selectedVersion !== null && selectedVersion.id !== activeVersionId;
  const versionQuery = selectedVersionId
    ? `?version=${encodeURIComponent(selectedVersionId)}`
    : "";

  const timetable = useQuery({
    queryKey: ["timetable", projectId, view, weekParity, effectiveVersionId],
    queryFn: async () =>
      (await getProjectRepository()).timetableForVersion<TimetableViewResponse>(
        projectId,
        effectiveVersionId,
        view,
        weekParity
      )
  });
  const courses = useQuery({
    queryKey: ["courses", projectId],
    queryFn: () =>
      apiRequest<CatalogList<CourseItem>>(
        `/projects/${projectId}/courses`
      )
  });
  const teachers = useQuery({
    queryKey: ["teachers", projectId],
    queryFn: () =>
      apiRequest<CatalogList<TeacherItem>>(
        `/projects/${projectId}/teachers`
      )
  });
  const classes = useQuery({
    queryKey: ["classes", projectId],
    queryFn: () =>
      apiRequest<CatalogList<ClassItem>>(
        `/projects/${projectId}/classes`
      )
  });
  const rooms = useQuery({
    queryKey: ["rooms", projectId],
    queryFn: () =>
      apiRequest<CatalogList<RoomItem>>(
        `/projects/${projectId}/room-types`
      )
  });

  async function refreshTimetable(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["timetable", projectId, "CLASS"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["timetable", projectId, "TEACHER"]
      }),
      queryClient.invalidateQueries({
        queryKey: ["timetable", projectId, "ROOM"]
      }),
      queryClient.invalidateQueries({ queryKey: ["timetable-versions", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["timetable-version-head", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
    ]);
  }

  const write = useMutation({
    mutationFn: async (command: WriteCommand) => {
      if (historicalVersion) {
        throw new ProjectRepositoryError(
          "HISTORICAL_VERSION_READ_ONLY",
          "历史课表版本仅供查看和导出，不能修改"
        );
      }
      if (command.kind === "SAVE") {
        const path = command.lessonId
          ? `/projects/${projectId}/timetable/lessons/${command.lessonId}`
          : `/projects/${projectId}/timetable/lessons`;
        return apiRequest(path, {
          method: command.lessonId ? "PATCH" : "POST",
          body: JSON.stringify({
            view,
            object_id: command.contextObjectId,
            week_parity: weekParity,
            weekday: command.payload.weekday,
            time_slot_id: command.payload.timeSlotId,
            course_id: command.payload.courseId,
            class_id: command.payload.classId,
            main_teacher_id: command.payload.mainTeacherId,
            assistant_teacher_ids: command.payload.assistantTeacherIds,
            room_instance_id: command.payload.roomInstanceId || null,
            week_pattern: command.payload.weekPattern,
            expected_timetable_revision: command.expectedRevision,
            confirm_rule_warnings: command.confirmRuleWarnings,
            __timetable_version_id: effectiveVersionId
          })
        });
      }
      if (command.kind === "DELETE") {
        return apiRequest(
          `/projects/${projectId}/timetable/lessons/${command.lessonId}` +
            `?expected_timetable_revision=${command.expectedRevision}` +
            `&confirm=true&__timetable_version_id=${effectiveVersionId ?? ""}`,
          { method: "DELETE" }
        );
      }
      if (command.kind === "MOVE") {
        return apiRequest(`/projects/${projectId}/timetable/moves`, {
          method: "POST",
          body: JSON.stringify({
            view,
            object_id: command.contextObjectId,
            week_parity: weekParity,
            lesson_id: command.action.sourceLesson.id,
            weekday: command.action.target.weekday,
            time_slot_id: command.action.target.time_slot_id,
            expected_timetable_revision: command.action.expectedRevision,
            confirm_rule_warnings: command.confirmRuleWarnings,
            __timetable_version_id: effectiveVersionId
          })
        });
      }
      return apiRequest(`/projects/${projectId}/timetable/swaps`, {
        method: "POST",
        body: JSON.stringify({
          view,
          object_id: command.contextObjectId,
          week_parity: weekParity,
          source_lesson_id: command.action.sourceLesson.id,
          target_lesson_id: command.action.target.target_lesson_id,
          expected_timetable_revision: command.action.expectedRevision,
          confirm_swap: true,
          confirm_rule_warnings: command.confirmRuleWarnings,
          __timetable_version_id: effectiveVersionId
        })
      });
    },
    onSuccess: async () => {
      setEditor(null);
      setForm(null);
      setDeleting(null);
      setConfirmation(null);
      await refreshTimetable();
    },
    onError: async (error, command) => {
      if (
        error instanceof ApiError &&
        isRuleWarningConfirmation(error)
      ) {
        write.reset();
        setConfirmation({
          title: "确认违反排课规则",
          description: warningDescription(error),
          command: withRuleWarningConfirmation(command)
        });
        return;
      }
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await refreshTimetable();
      }
    }
  });

  // 默认（未手动选择）时聚焦第一个对象，突出单张画布；
  // 选择「全部」后 activeObjectId 为 ""，沿用连续查看全部对象的路径。
  const activeObjectId =
    selectedObjectId ?? timetable.data?.schedules[0]?.object.id ?? "";

  const exportMutation = useMutation({
    mutationFn: ({ scope, acknowledge }: {
      scope: "CURRENT" | "ALL";
      acknowledge: boolean;
    }) =>
      apiDownload(`/projects/${projectId}/exports`, {
        view,
        scope,
        object_id: scope === "CURRENT" ? activeObjectId : null,
        acknowledge_replan_warning: acknowledge,
        __timetable_version_id: effectiveVersionId
      })
  });

  const data = timetable.data;
  const shownSchedules = useMemo(
    () =>
      activeObjectId
        ? (data?.schedules ?? []).filter(
            (item) => item.object.id === activeObjectId
          )
        : (data?.schedules ?? []),
    [data?.schedules, activeObjectId]
  );
  const roomInstances = (rooms.data?.items ?? []).flatMap(
    (room) => room.instances
  );

  function openAdd(
    schedule: TimetableSchedule,
    weekday: number,
    slot: TimetableSlot
  ): void {
    if (!data || schedule.object.is_deleted || historicalVersion) return;
    write.reset();
    setForm({
      weekday,
      timeSlotId: slot.id,
      courseId: "",
      classId: view === "CLASS" ? schedule.object.id : "",
      mainTeacherId: view === "TEACHER" ? schedule.object.id : "",
      assistantTeacherIds: [],
      roomInstanceId: view === "ROOM" ? schedule.object.id : "",
      weekPattern: weekParity
    });
    setEditor({
      schedule,
      lesson: null,
      expectedRevision: data.timetable_revision
    });
  }

  function openEdit(
    schedule: TimetableSchedule,
    lesson: TimetableLesson
  ): void {
    if (!data || historicalVersion) return;
    write.reset();
    setForm({
      weekday: lesson.weekday,
      timeSlotId: lesson.time_slot_id,
      courseId: lesson.course.id,
      classId: lesson.school_class.id,
      mainTeacherId: lesson.main_teacher.id,
      assistantTeacherIds: lesson.assistant_teachers.map((teacher) => teacher.id),
      roomInstanceId: lesson.room_instance?.id ?? "",
      weekPattern: lesson.week_pattern
    });
    setEditor({
      schedule,
      lesson,
      expectedRevision: data.timetable_revision
    });
  }

  function submitEditor(): void {
    if (!editor || !form) return;
    write.mutate({
      kind: "SAVE",
      lessonId: editor.lesson?.id ?? null,
      expectedRevision: editor.expectedRevision,
      confirmRuleWarnings: false,
      payload: form,
      contextObjectId: editor.schedule.object.id
    });
  }

  function handlePlacement(
    schedule: TimetableSchedule,
    action: PlacementAction
  ): void {
    if (historicalVersion) return;
    write.reset();
    const command: WriteCommand =
      action.kind === "MOVE"
        ? {
            kind: "MOVE",
            action,
            contextObjectId: schedule.object.id,
            confirmRuleWarnings: false
          }
        : {
            kind: "SWAP",
            action,
            contextObjectId: schedule.object.id,
            confirmRuleWarnings: action.target.warnings.length > 0
          };
    if (action.kind === "SWAP") {
      setConfirmation({
        title: "交换两节课程",
        description:
          `确认把“${action.sourceLesson.course.label}”与目标课程交换时间？` +
          warningSuffix(action.target),
        command
      });
      return;
    }
    if (action.target.warnings.length > 0) {
      setConfirmation({
        title: "确认违反排课规则",
        description:
          action.target.warnings.map((item) => item.message).join("；") +
          "。确认后移动课程。",
        command: { ...command, confirmRuleWarnings: true }
      });
      return;
    }
    write.mutate(command);
  }

  function startExport(scope: "CURRENT" | "ALL"): void {
    exportMutation.reset();
    continueExport({ scope });
  }

  function continueExport(request: ExportRequestState): void {
    if (data?.needs_replan) {
      setExportConfirmation(request);
      return;
    }
    exportMutation.mutate({ ...request, acknowledge: false });
  }

  if (timetable.isLoading) {
    return (
      <main className="page-shell">
        <p className="state-message">正在加载课程表…</p>
      </main>
    );
  }
  if (!data || timetable.isError) {
    return (
      <main className="page-shell">
        <a className="back-link" href={`/projects/${projectId}`}>
          ← 返回课表主页
        </a>
        <section className="empty-card compact">
          <h1>暂时无法查看课程表</h1>
          <ErrorText error={timetable.error} />
        </section>
      </main>
    );
  }

  return (
    <main className="page-shell timetable-page">
      <ContextHeader
        projectId={projectId}
        title={`${viewOption(view).label}课程表`}
        backTo={{ href: `/projects/${projectId}`, label: "返回课表主页" }}
        actions={
          <>
            {TIMETABLE_VIEWS.filter((item) => item.view !== view).map((item) => (
              <a
                className="ghost-button"
                href={`/projects/${projectId}/timetable/${item.segment}${versionQuery}`}
                key={item.view}
              >
                查看{item.label}课表
              </a>
            ))}
            <button
              className="ghost-button"
              type="button"
              disabled={!activeObjectId || exportMutation.isPending}
              onClick={() => startExport("CURRENT")}
            >
              导出当前
            </button>
            <button
              className="solid-button"
              type="button"
              disabled={exportMutation.isPending}
              onClick={() => startExport("ALL")}
            >
              {exportMutation.isPending ? "正在生成…" : "导出全部"}
            </button>
          </>
        }
      />

      {(versions.data?.length ?? 0) > 0 ? (
        <section className="timetable-version-toolbar" aria-label="选择课表版本">
          <label className="field">
            <span>课表版本</span>
            <select
              value={effectiveVersionId ?? ""}
              onChange={(event) => {
                const versionId = event.target.value || null;
                setSelectedVersionId(versionId);
                const url = new URL(window.location.href);
                if (versionId) url.searchParams.set("version", versionId);
                else url.searchParams.delete("version");
                window.history.replaceState(null, "", url);
                setSelectedObjectId(null);
                setEditor(null);
                setForm(null);
                setDeleting(null);
                setConfirmation(null);
                setVersionDetailsOpen(false);
              }}
            >
              {(versions.data ?? []).map((version) => (
                <option value={version.id} key={version.id}>
                  {version.label} · {version.completeness === "COMPLETE"
                    ? "完整"
                    : `未排完整 · ${version.scheduled_lessons}/${version.required_lessons}`}
                  {version.id === activeVersionId ? " · 当前" : ""}
                </option>
              ))}
            </select>
          </label>
          <div>
            <strong>{selectedVersion?.label ?? "当前课表"}</strong>
            <span>
              {historicalVersion
                ? "历史版本只读，可查看和导出"
                : "当前活动版本，可继续人工微调"}
            </span>
          </div>
        </section>
      ) : null}

      {selectedVersion?.completeness === "INCOMPLETE" ? (
        <section className="warning-banner incomplete-version-banner" role="status">
          <div>
            <h2>
              本课表未排完整：已排 {selectedVersion.scheduled_lessons}/
              {selectedVersion.required_lessons} 节，仍有 {Math.max(
                0,
                selectedVersion.required_lessons - selectedVersion.scheduled_lessons
              )} 节未排
            </h2>
            <p>这份课表可以继续查看、导出；未排内容和当时调整会随版本长期保留。</p>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setVersionDetailsOpen(true)}
          >
            查看未满足条件
          </button>
        </section>
      ) : selectedVersion && selectedVersion.actual_repairs.length > 0 ? (
        <section className="accepted-relaxation-banner">
          <div>
            <strong>本版本采用时调整了 {selectedVersion.actual_repairs.length} 项条件</strong>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setVersionDetailsOpen(true)}
          >
            查看详情
          </button>
        </section>
      ) : null}

      {historicalVersion ? (
        <p className="historical-version-note" role="status">
          当前查看的是历史版本；新增、修改、移动、交换和删除课程均已停用。
        </p>
      ) : null}

      {data.needs_replan ? (
        <section className="warning-banner" aria-labelledby="replan-title">
          <h2 id="replan-title">当前课表仍可查看和调整，但建议重新排课</h2>
          <ul>
            {data.replan_reasons.map((reason) => (
              <li key={reason.id}>{reason.message}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.accepted_relaxation_count > 0 ? (
        <details className="accepted-relaxation-banner">
          <summary>
            本课表采用时放宽了 {data.accepted_relaxation_count} 项条件
            <span>查看明细</span>
          </summary>
          <ul>
            {data.accepted_relaxations.map((item) => (
              <li key={item.event_id}>
                <strong>{item.message_snapshot}</strong>
                <span>原要求：{item.original_value}</span>
                <span>实际安排：{item.actual_value}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {data.course_rule_override_count > 0 ? (
        <section
          className="course-rule-override-banner"
          aria-labelledby="course-rule-override-title"
        >
          <div>
            <h2 id="course-rule-override-title">
              人工覆盖规则 {data.course_rule_override_count} 处
            </h2>
            <p>相关课程已在课表中标记；调回合规位置后会自动移除。</p>
          </div>
          <details>
            <summary>查看具体位置</summary>
            <ul>
              {data.course_rule_overrides.map((override) => (
                <li
                  key={`${override.code}:${override.class_id}:${override.weekday}:${override.lesson_ids.join(":")}`}
                >
                  {override.message}
                </li>
              ))}
            </ul>
          </details>
        </section>
      ) : null}

      <ErrorText error={exportMutation.error} />
      {write.error &&
      !confirmation &&
      !(
        write.error instanceof ApiError &&
        isRuleWarningConfirmation(write.error)
      ) ? (
        <ErrorText error={write.error} />
      ) : null}

      <div className="timetable-layout">
        <nav
          className="object-nav"
          aria-label={`选择${viewOption(view).label}`}
        >
          <h3>{viewOption(view).label}</h3>
          <button
            type="button"
            className={selectedObjectId === "" ? "active" : ""}
            onClick={() => setSelectedObjectId("")}
          >
            全部{viewOption(view).label}
          </button>
          {data.schedules.map((schedule) => (
            <button
              type="button"
              key={schedule.object.id}
              className={
                schedule.object.id === activeObjectId ? "active" : ""
              }
              onClick={() => setSelectedObjectId(schedule.object.id)}
            >
              {referenceLabel(schedule.object)}
            </button>
          ))}
        </nav>

        <section className="canvas-shell" aria-label="课表画布">
          <div className="canvas-toolbar">
            <div className="week-parity-switch" aria-label="查看周次">
              {(["ODD", "EVEN"] as const).map((parity) => (
                <button
                  type="button"
                  key={parity}
                  className={weekParity === parity ? "active" : ""}
                  aria-pressed={weekParity === parity}
                  onClick={() => {
                    setWeekParity(parity);
                    setEditor(null);
                    setForm(null);
                    setDeleting(null);
                    setConfirmation(null);
                  }}
                >
                  {weekParityLabel(parity)}
                </button>
              ))}
            </div>
            <label className="field">
              <span>筛选{viewOption(view).label}</span>
              <select
                value={activeObjectId}
                onChange={(event) =>
                  setSelectedObjectId(event.target.value)
                }
              >
                <option value="">
                  全部{viewOption(view).label}
                </option>
                {data.schedules.map((schedule) => (
                  <option value={schedule.object.id} key={schedule.object.id}>
                    {referenceLabel(schedule.object)}
                  </option>
                ))}
              </select>
            </label>
            <p className="canvas-status">
              {weekParityLabel(data.week_parity)} · 当前显示 {shownSchedules.length} 个
              {viewOption(view).label} · 课表修订{" "}
              {data.timetable_revision}
            </p>
          </div>
          <p className="scroll-hint" role="note">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M8 7l-4 5 4 5M16 7l4 5-4 5" />
            </svg>
            左右滑动查看完整课表
          </p>
          <div className="timetable-schedule-list">
            {shownSchedules.map((schedule) =>
              historicalVersion ? (
                <TimetableGrid
                  mode="READ_ONLY"
                  key={`${weekParity}:${schedule.object.id}`}
                  view={view}
                  schedule={schedule}
                  slots={data.slots}
                  timetableRevision={data.timetable_revision}
                />
              ) : (
                <TimetableGrid
                  key={`${weekParity}:${schedule.object.id}`}
                  view={view}
                  schedule={schedule}
                  slots={data.slots}
                  timetableRevision={data.timetable_revision}
                  pending={write.isPending}
                  requestOptions={(lessonId) =>
                    apiRequest<DragOptionsResponse>(
                      `/projects/${projectId}/timetable/drag-options`,
                      {
                        method: "POST",
                        body: JSON.stringify({
                          view,
                          object_id: schedule.object.id,
                          week_parity: weekParity,
                          lesson_id: lessonId,
                          expected_timetable_revision: data.timetable_revision
                        })
                      }
                    )
                  }
                  onAdd={(weekday, slot) => openAdd(schedule, weekday, slot)}
                  onEdit={(lesson) => openEdit(schedule, lesson)}
                  onDelete={(lesson) => {
                    write.reset();
                    setDeleting({
                      lesson,
                      expectedRevision: data.timetable_revision
                    });
                  }}
                  onPlacement={(action) => handlePlacement(schedule, action)}
                />
              )
            )}
          </div>
          {!historicalVersion ? (
            <details className="timetable-help">
              <summary>拖拽与微调说明</summary>
              <p>
                拖动课程仅会开放可移动或可交换的位置；人工调整可以突破计划规则，
                但班级、教师和功能教室冲突始终禁止。空位可直接添加课程，
                课程卡片支持修改、移动/交换和删除。
              </p>
            </details>
          ) : null}
        </section>

        <aside
          className={`overview-panel timetable-overview-panel ${overviewOpen ? "is-open" : "is-collapsed"}`}
          aria-label="课程总览"
        >
          <div className="overview-head">
            {overviewOpen ? <h3>课程总览</h3> : null}
            <button
              className="mini-button"
              type="button"
              aria-expanded={overviewOpen}
              aria-label={overviewOpen ? "收起课程总览" : "展开课程总览"}
              onClick={() => setOverviewOpen((open) => !open)}
            >
              {overviewOpen ? "收起" : "课程总览"}
            </button>
          </div>
          {overviewOpen ? (
            <div className="overview-body">
              {shownSchedules.map((schedule) => (
                <section className="overview-group" key={schedule.object.id}>
                  {shownSchedules.length > 1 ? (
                    <h4>{referenceLabel(schedule.object)}</h4>
                  ) : null}
                  <ScheduleOverview view={view} schedule={schedule} />
                </section>
              ))}
            </div>
          ) : null}
        </aside>
      </div>

      {editor && form ? (
        <LessonEditor
          view={view}
          editor={editor}
          form={form}
          slots={data.slots}
          courses={courses.data?.items ?? []}
          teachers={teachers.data?.items ?? []}
          classes={classes.data?.items ?? []}
          rooms={roomInstances}
          pending={write.isPending}
          error={write.error}
          onChange={setForm}
          onClose={() => {
            setEditor(null);
            setForm(null);
          }}
          onSubmit={submitEditor}
        />
      ) : null}
      {versionDetailsOpen && selectedVersion ? (
        <VersionDetailsModal
          version={selectedVersion}
          onClose={() => setVersionDetailsOpen(false)}
        />
      ) : null}
      {deleting ? (
        <ConfirmModal
          title="删除课程"
          description={`确认删除“${deleting.lesson.course.label}”这节${weekPatternLabel(deleting.lesson.week_pattern)}课程？删除只改变当前课表，不会修改任课计划。`}
          dangerLabel="确认删除"
          pending={write.isPending}
          error={write.error}
          onClose={() => setDeleting(null)}
          onConfirm={() =>
            write.mutate({
              kind: "DELETE",
              lessonId: deleting.lesson.id,
              expectedRevision: deleting.expectedRevision
            })
          }
        />
      ) : null}
      {confirmation ? (
        <ActionConfirmModal
          title={confirmation.title}
          description={confirmation.description}
          pending={write.isPending}
          error={write.error}
          onClose={() => setConfirmation(null)}
          onConfirm={() => write.mutate(confirmation.command)}
        />
      ) : null}
      {exportConfirmation ? (
        <ActionConfirmModal
          title="仍要导出课表？"
          description="当前课表提示需重新排课。导出文件会写入提示和全部原因，确认后仍可正常导出。"
          pending={exportMutation.isPending}
          error={exportMutation.error}
          onClose={() => setExportConfirmation(null)}
          onConfirm={() =>
            exportMutation.mutate(
              { ...exportConfirmation, acknowledge: true },
              { onSuccess: () => setExportConfirmation(null) }
            )
          }
        />
      ) : null}
    </main>
  );
}

function versionIdFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get("version");
}

function VersionDetailsModal({
  version,
  onClose
}: {
  version: TimetableVersionRecord;
  onClose: () => void;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, true);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-card wide timetable-version-details"
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-details-title"
      >
        <h2 id="version-details-title">{version.label} · 排课条件说明</h2>
        <div className="version-detail-summary">
          <strong>
            {version.completeness === "COMPLETE"
              ? "完整课表"
              : `未排完整：已排 ${version.scheduled_lessons}/${version.required_lessons} 节`}
          </strong>
          <span>求解状态：{version.solver_status ?? "历史课表未记录"}</span>
        </div>
        <section>
          <h3>本版本实际调整的条件</h3>
          {version.actual_repairs.length ? (
            <ul>
              {version.actual_repairs.map((value, index) => {
                const item = recordValue(value);
                return (
                  <li key={String(item.repair_id ?? item.event_id ?? index)}>
                    <strong>{String(item.title ?? item.message ?? "条件调整")}</strong>
                    <span>原要求：{String(item.original_value ?? "未记录")}</span>
                    <span>
                      本版本：{String(item.proposed_value ?? item.actual_value ?? "未记录")}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>本版本没有记录可调整条件的变化。</p>
          )}
        </section>
        <section>
          <h3>未排课程及阻塞因素</h3>
          {version.unassigned_requirements.length ? (
            <ul>
              {version.unassigned_requirements.map((value, index) => {
                const item = recordValue(value);
                return (
                  <li key={String(item.plan_id ?? index)}>
                    <strong>
                      {String(item.class_name ?? "未命名班级")} · {String(item.course_name ?? "未命名课程")}
                    </strong>
                    <span>
                      已排 {String(item.scheduled_lessons ?? 0)} / {String(item.required_lessons ?? 0)} 节，
                      未排 {String(item.unassigned_lessons ?? 0)} 节
                    </span>
                    {Array.isArray(item.blockers) ? (
                      <ul>
                        {item.blockers.map((blocker, blockerIndex) => (
                          <li key={blockerIndex}>
                            {String(recordValue(blocker).message ?? "多项条件共同影响")}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>没有未排课程。</p>
          )}
        </section>
        <div className="modal-actions">
          <button className="primary-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
      </section>
    </div>
  );
}

function LessonEditor({
  view,
  editor,
  form,
  slots,
  courses,
  teachers,
  classes,
  rooms,
  pending,
  error,
  onChange,
  onClose,
  onSubmit
}: {
  view: TimetableView;
  editor: EditorState;
  form: LessonForm;
  slots: TimetableSlot[];
  courses: CourseItem[];
  teachers: TeacherItem[];
  classes: ClassItem[];
  rooms: RoomInstance[];
  pending: boolean;
  error: unknown;
  onChange: (form: LessonForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}): React.JSX.Element {
  const lesson = editor.lesson;
  const creating = lesson === null;
  const fixedClass = view === "CLASS";
  const fixedMain =
    view === "TEACHER" && (creating || lesson?.teacher_role === "MAIN");
  const fixedAssistant =
    view === "TEACHER" && lesson?.teacher_role === "ASSISTANT";
  const fixedRoom = view === "ROOM";
  const assistantOptions = [
    ...teachers,
    ...(lesson?.assistant_teachers ?? [])
      .filter((item) => !teachers.some((teacher) => teacher.id === item.id))
      .map((item) => ({ id: item.id, name: item.label }))
  ];
  const mainTeacherReference = fixedMain
    ? editor.schedule.object
    : lesson?.main_teacher;
  const mainTeacherOptions = [
    ...teachers,
    ...(mainTeacherReference &&
    !teachers.some((teacher) => teacher.id === mainTeacherReference.id)
      ? [{ id: mainTeacherReference.id, name: mainTeacherReference.label }]
      : [])
  ];
  return (
    <EditorModal
      wide
      title={creating ? "添加一节课程" : "修改课程"}
      submitLabel={creating ? "添加课程" : "保存修改"}
      pending={pending}
      error={error}
      onClose={onClose}
      onSubmit={onSubmit}
      submitDisabled={!form.mainTeacherId}
    >
      <div className="plan-form-grid">
        <label className="field">
          <span>课程</span>
          <select
            required
            value={form.courseId}
            onChange={(event) =>
              onChange({ ...form, courseId: event.target.value })
            }
          >
            <option value="">请选择</option>
            {referenceOption(lesson?.course, courses)}
            {courses.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>授课班级{fixedClass ? "（当前课表固定）" : ""}</span>
          <select
            required
            disabled={fixedClass}
            value={form.classId}
            onChange={(event) =>
              onChange({ ...form, classId: event.target.value })
            }
          >
            <option value="">请选择</option>
            {referenceOption(
              fixedClass ? editor.schedule.object : lesson?.school_class,
              classes.map((item) => ({
                id: item.id,
                name: `${item.grade_label}${item.class_name}`
              }))
            )}
            {classes.map((item) => (
              <option value={item.id} key={item.id}>
                {item.grade_label}
                {item.class_name}
              </option>
            ))}
          </select>
        </label>
        <TeacherSearchSelect
          label={`主教老师${fixedMain ? "（当前教师固定）" : ""}`}
          disabled={fixedMain}
          value={form.mainTeacherId}
          options={mainTeacherOptions}
          onChange={(mainTeacherId) =>
            onChange({
              ...form,
              mainTeacherId,
              assistantTeacherIds: form.assistantTeacherIds.filter(
                (id) => id !== mainTeacherId
              )
            })
          }
        />
        <AssistantMultiSelect
          value={form.assistantTeacherIds}
          options={assistantOptions}
          fixedIds={fixedAssistant ? [editor.schedule.object.id] : []}
          disabledIds={form.mainTeacherId ? [form.mainTeacherId] : []}
          onChange={(assistantTeacherIds) =>
            onChange({ ...form, assistantTeacherIds })
          }
        />
        <label className="field">
          <span>上课周次</span>
          <select
            value={form.weekPattern}
            onChange={(event) =>
              onChange({
                ...form,
                weekPattern: event.target.value as WeekPattern
              })
            }
          >
            {weekPatternOptions.map((item) => (
              <option value={item.value} key={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>指定功能教室{fixedRoom ? "（当前功能教室固定）" : ""}</span>
          <select
            disabled={fixedRoom}
            value={form.roomInstanceId}
            onChange={(event) =>
              onChange({ ...form, roomInstanceId: event.target.value })
            }
          >
            <option value="">无</option>
            {referenceOption(
              fixedRoom ? editor.schedule.object : lesson?.room_instance,
              rooms
            )}
            {rooms.map((item) => (
              <option value={item.id} key={item.id}>
                {item.display_name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>星期{creating ? "（所选空位固定）" : ""}</span>
          <select
            disabled={creating}
            value={form.weekday}
            onChange={(event) =>
              onChange({ ...form, weekday: Number(event.target.value) })
            }
          >
            {["星期一", "星期二", "星期三", "星期四", "星期五"].map(
              (label, index) => (
                <option value={index + 1} key={label}>
                  {label}
                </option>
              )
            )}
          </select>
        </label>
        <label className="field">
          <span>节次{creating ? "（所选空位固定）" : ""}</span>
          <select
            disabled={creating}
            value={form.timeSlotId}
            onChange={(event) =>
              onChange({ ...form, timeSlotId: event.target.value })
            }
          >
            {slots.map((slot) => (
              <option value={slot.id} key={slot.id}>
                {slot.day_part === "AM" ? "上午" : "下午"} ·{" "}
                {slot.display_time}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="field-hint">
        手动保存不会修改任课计划，也不要求新增课程对应任课计划。
      </p>
    </EditorModal>
  );
}

function ActionConfirmModal({
  title,
  description,
  pending,
  error,
  onClose,
  onConfirm
}: {
  title: string;
  description: string;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose, !pending);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-confirm-title"
      >
        <h2 id="action-confirm-title">{title}</h2>
        <p>{description}</p>
        <ErrorText error={error} />
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            取消
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? "正在处理…" : "确认继续"}
          </button>
        </div>
      </section>
    </div>
  );
}

function referenceOption(
  reference: TimetableReference | null | undefined,
  activeItems: Array<{ id: string; name?: string; display_name?: string }>
): React.JSX.Element | null {
  if (!reference || activeItems.some((item) => item.id === reference.id)) {
    return null;
  }
  return (
    <option value={reference.id}>
      {reference.label}
      {reference.is_deleted ? "（已删除，仅可保留）" : ""}
    </option>
  );
}

function referenceLabel(reference: TimetableReference): string {
  return `${reference.label}${reference.is_deleted ? "（已删除）" : ""}`;
}

function viewOption(view: TimetableView): (typeof TIMETABLE_VIEWS)[number] {
  return TIMETABLE_VIEWS.find((item) => item.view === view) ?? TIMETABLE_VIEWS[0];
}

function withRuleWarningConfirmation(command: WriteCommand): WriteCommand {
  if (
    command.kind === "SAVE" ||
    command.kind === "MOVE" ||
    command.kind === "SWAP"
  ) {
    return { ...command, confirmRuleWarnings: true };
  }
  return command;
}

function isRuleWarningConfirmation(error: ApiError): boolean {
  return (
    error.code === "RULE_WARNING_CONFIRMATION_REQUIRED" ||
    error.code === "UNAVAILABILITY_CONFIRMATION_REQUIRED"
  );
}

function warningDescription(error: ApiError): string {
  const warnings = error.details["warnings"];
  if (!Array.isArray(warnings)) {
    return "该操作违反排课规则。确认后才会保存。";
  }
  const messages = warnings
    .map((item) =>
      typeof item === "object" &&
      item !== null &&
      "message" in item &&
      typeof item.message === "string"
        ? item.message
        : null
    )
    .filter((item): item is string => item !== null);
  return `${messages.join("；")}。确认后才会保存。`;
}

function warningSuffix(target: PlacementAction["target"]): string {
  if (target.warnings.length === 0) return "";
  return ` 此交换还会覆盖：${target.warnings
    .map((item) => item.message)
    .join("；")}。`;
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
